import {
  Search as SearchIcon,
  StarRate as StarRateIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { yellow } from '@mui/material/colors';
import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  Dispatch,
  SetStateAction
} from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { useAdditionalFieldsController } from 'api/controllers/AdditionalFieldsController';
import { useCounterPartyController } from 'api/controllers/CounterPartyController';

import { CounterpartyDropDown, CounterpartyType, Invoice } from 'openapi';

import { useCompanies } from 'context/CompanyContext';
import { useTranslations } from 'context/TranslationContext';

import { useDebounce } from 'hooks/useDebounce';

import {
  COUNTERPARTY_IBAN_ADDITIONAL_INITIAL_FIELDS,
  INITIAL_IBAN_ITEM
} from 'utils/constants/constants';
import { COUNTERPARTY_SEARCH_MIN_CHARACTERS } from 'utils/constants/invoices';
import { uniqueIbanValidation } from 'utils/helpers/ibanHelper';
import {
  getCounterPartySearchOptionLabel,
  validateIban,
  validateRegistrationNumber,
  validateVatNumber
} from 'utils/helpers/invoiceHelpers';

import { arrayFieldFormContainer } from 'styles/components/Common';
import {
  fullWidth,
  smallVerificationFieldWidth,
  verificationFormFields,
  verificationFormFieldsWrapper,
  verificationFormItem
} from 'styles/pages/InvoiceVerificationStyle';

interface CounterpartyFormSectionProps {
  setSelectedCounterPartyId: Dispatch<SetStateAction<number | null>>;
  setIsExisting: Dispatch<SetStateAction<boolean>>;
  setCpIsNotFoundInDatabase: Dispatch<React.SetStateAction<boolean>>;
  isReimbursement?: boolean;
}

export const CounterpartyFormSection = ({
  setSelectedCounterPartyId,
  setIsExisting,
  setCpIsNotFoundInDatabase,
  isReimbursement
}: CounterpartyFormSectionProps) => {
  const {
    control,
    setValue,
    clearErrors,
    watch,
    getValues,
    trigger,
    formState
  } = useFormContext<Invoice>();

  const { append } = useFieldArray({
    control,
    name: 'ibans',
    keyName: 'fieldId'
  });

  const { translate } = useTranslations();
  const { companyId } = useParams();
  const companyName = useCompanies().companiesList.find((company) => {
    return company.id === Number(companyId);
  })?.name;

  const { checkIfCounterpartyExists } = useCounterPartyController();
  const { getAllDropDownCounterpartiesByCompany } =
    useAdditionalFieldsController();

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<CounterpartyDropDown[]>([]);
  const [searchParams, setSearchParams] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [isFoundInDatabase, setIsFoundInDatabase] = useState<boolean>(true);

  const notEnoughCharactersToSearch = useMemo(() => {
    return (searchParams || '').length < COUNTERPARTY_SEARCH_MIN_CHARACTERS;
  }, [searchParams]);

  const appendIban = () => {
    append({
      ...INITIAL_IBAN_ITEM,
      ...COUNTERPARTY_IBAN_ADDITIONAL_INITIAL_FIELDS,
      // @ts-ignore tmpId is used for unique identification for each IBAN (even newly created ones)
      tmpId: uuidv4()
    });
  };

  const getCounterParties = useCallback(async () => {
    if (notEnoughCharactersToSearch) {
      setIsLoading(false);
      setOptions([]);
      return;
    }

    const counterpartyType = isReimbursement
      ? CounterpartyType.REIMBURSEMENT
      : CounterpartyType.SUPPLIER;

    const result = await getAllDropDownCounterpartiesByCompany(
      Number(companyId),
      counterpartyType,
      searchParams
    );
    setOptions(result);
    setIsLoading(false);
  }, [
    getAllDropDownCounterpartiesByCompany,
    searchParams,
    notEnoughCharactersToSearch
  ]);

  const debouncedSearch = useDebounce(getCounterParties, 500);

  useEffect(() => {
    setIsLoading(true);
    debouncedSearch();
  }, [searchParams, debouncedSearch]);

  const watchedCounterpartyRegistrationNumber = watch(
    'counterpartyRegistrationNumber'
  );
  const watchedCounterpartyVatNumber = watch('counterpartyVatNumber');
  const watchedCounterpartyName = watch('counterpartyName');
  const watchedIbanList = watch('ibans');

  const isCounterpartyRegistrationNumberValid = useMemo(
    () =>
      validateRegistrationNumber(
        watchedCounterpartyVatNumber,
        watchedCounterpartyRegistrationNumber
      ),
    [watchedCounterpartyRegistrationNumber, watchedCounterpartyVatNumber]
  );

  const isCounterpartyVatValid = useMemo(
    () =>
      validateVatNumber(
        watchedCounterpartyVatNumber,
        watchedCounterpartyRegistrationNumber
      ),
    [
      watchedCounterpartyVatNumber,
      watchedCounterpartyRegistrationNumber,
      isCounterpartyRegistrationNumberValid
    ]
  );

  useEffect(() => {
    trigger(['counterpartyRegistrationNumber', 'counterpartyVatNumber']);
  }, [
    watchedCounterpartyVatNumber,
    watchedCounterpartyRegistrationNumber,
    trigger
  ]);

  const handleAutocompleteChange = useCallback(
    (selectedOption?: CounterpartyDropDown) => {
      if (!selectedOption) {
        return;
      }

      setSelectedCounterPartyId(selectedOption.id || null);
      setValue('counterpartyName', selectedOption.name || '');
      setValue(
        'counterpartyRegistrationNumber',
        selectedOption.registrationNumber || ''
      );
      setValue('counterpartyVatNumber', selectedOption.vatNumber || '');
      setValue(
        'ibans',
        selectedOption.ibans?.map((iban) => ({ ...iban, tmpId: uuidv4() })) || [
          {
            ...INITIAL_IBAN_ITEM,
            ...COUNTERPARTY_IBAN_ADDITIONAL_INITIAL_FIELDS,
            tmpId: uuidv4()
          }
        ]
      );
      clearErrors();
      trigger('counterpartyName');
    },
    [setSelectedCounterPartyId, setValue, clearErrors, trigger]
  );

  const checkExisting = useCallback(async () => {
    if (!companyId) {
      return;
    }

    const response = await checkIfCounterpartyExists(
      Number(companyId),
      getValues('counterpartyName') || '',
      getValues('counterpartyRegistrationNumber') || '',
      getValues('counterpartyVatNumber') || ''
    );

    setIsFoundInDatabase(response.isFoundInDatabase);
    setCpIsNotFoundInDatabase(!response.isFoundInDatabase);
    setIsExisting(response.isCompanyCounterparty);
  }, [getValues, checkIfCounterpartyExists]);

  const debouncedCheckExisting = useDebounce(checkExisting, 200);

  useEffect(() => {
    debouncedCheckExisting();
  }, [
    watchedCounterpartyName,
    watchedCounterpartyRegistrationNumber,
    watchedCounterpartyVatNumber,
    getValues
  ]);

  return (
    <Box sx={fullWidth}>
      <Box sx={verificationFormFields}>
        <Box
          sx={{
            ...verificationFormFieldsWrapper,
            height: '55px'
          }}
        >
          <Controller
            name="counterpartyId"
            control={control}
            render={({ field }) => (
              <FormControl sx={verificationFormItem}>
                <Autocomplete
                  open={
                    open &&
                    !isLoading &&
                    (notEnoughCharactersToSearch || options.length > 0)
                  }
                  disableClearable
                  blurOnSelect
                  clearOnBlur={false}
                  forcePopupIcon={false}
                  onChange={(_, selectedOption) => {
                    handleAutocompleteChange(selectedOption);
                  }}
                  onInputChange={(_, value) => {
                    setSearchParams(value);
                    setIsLoading(true);
                  }}
                  onOpen={() => {
                    setOpen(true);
                  }}
                  onClose={(_event, reason) => {
                    if (reason === 'escape' || reason === 'blur') {
                      setOpen(false);
                    }
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={(option) =>
                    getCounterPartySearchOptionLabel(option)
                  }
                  options={options}
                  loading={notEnoughCharactersToSearch ? false : isLoading}
                  noOptionsText={
                    notEnoughCharactersToSearch
                      ? translate('messages.searchRequiredCharacters', {
                          numberOfCharacters: String(
                            COUNTERPARTY_SEARCH_MIN_CHARACTERS
                          )
                        })
                      : translate('labels.noCounterpartiesFound')
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...field}
                      label={translate('labels.counterparty')}
                      onChange={(event) => {
                        setSearchParams(event.target.value);
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment sx={{ ml: 1 }} position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            {isLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                          </InputAdornment>
                        )
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Tooltip
                      key={props.key}
                      title={
                        option.isCompanyCounterparty &&
                        `${translate('messages.counterpartyOf', {
                          companyName: companyName || ''
                        })}`
                      }
                    >
                      <li {...props}>
                        {getCounterPartySearchOptionLabel(option)}
                        {option.isCompanyCounterparty && (
                          <StarRateIcon
                            color="primary"
                            sx={{ ml: 1, mb: 0.5 }}
                          />
                        )}
                      </li>
                    </Tooltip>
                  )}
                />
              </FormControl>
            )}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            opacity: !isFoundInDatabase && formState.isValid ? 1 : 0
          }}
        >
          <WarningIcon
            sx={{
              fontSize: 26,
              color: yellow[900]
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: '0.6rem', sm: '0.8rem' },
              textAlign: 'left'
            }}
          >
            {translate('messages.counterpartyNotInDatabase')}
          </Typography>
        </Box>
        <Box
          sx={{
            ...verificationFormFieldsWrapper,
            minHeight: '50px'
          }}
        >
          <FormControl sx={verificationFormItem}>
            <Controller
              name="counterpartyName"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={`${
                    isReimbursement
                      ? translate('labels.reimburseTo')
                      : translate('labels.name')
                  }*`}
                  name="counterpartyName"
                  placeholder={translate('labels.name')}
                  error={!!error}
                  helperText={error && translate('errors.counterpartyName')}
                />
              )}
            />
          </FormControl>
        </Box>
        {isReimbursement && (
          <Box
            sx={{
              ...verificationFormFieldsWrapper,
              minHeight: '50px',
              flexDirection: 'column',
              marginTop: 1
            }}
          >
            {watchedIbanList?.map((item, index) => (
              <Box
                sx={{ ...arrayFieldFormContainer, alignItems: 'center' }}
                // @ts-ignore tmpId is used for unique identification for each IBAN (even newly created ones)
                key={item.tmpId}
              >
                <Box sx={{ ...verificationFormFields, width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: 70
                    }}
                  >
                    <Controller
                      name={`ibans.${index}.iban`}
                      control={control}
                      rules={{
                        validate: (value) =>
                          value
                            ? validateIban(value) &&
                              uniqueIbanValidation(
                                translate,
                                getValues(`ibans.${index}.iban`),
                                getValues('ibans')
                              )
                            : true
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            trigger();
                          }}
                          label={translate('labels.iban')}
                          error={
                            !!formState.errors.ibans &&
                            !!formState.errors.ibans[index]
                          }
                          placeholder={translate('labels.iban')}
                          sx={smallVerificationFieldWidth}
                        />
                      )}
                    />
                    {formState.errors.ibans &&
                      formState.errors.ibans[index] && (
                        <FormHelperText error>
                          {!validateIban(getValues(`ibans.${index}.iban`) || '')
                            ? translate('errors.invalidIban')
                            : translate('errors.ibanUnique')}
                        </FormHelperText>
                      )}
                  </Box>
                </Box>
              </Box>
            ))}
            <Box display="flex" justifyContent="center">
              <Button type="button" onClick={appendIban}>
                {`+ ${translate('buttons.add')}`}
              </Button>
            </Box>
          </Box>
        )}
        {!isReimbursement && (
          <Box
            sx={{
              ...verificationFormFieldsWrapper,
              height: 85
            }}
          >
            <Controller
              name="counterpartyRegistrationNumber"
              control={control}
              rules={{
                validate: () => {
                  if (!watchedCounterpartyVatNumber) {
                    return (
                      isCounterpartyRegistrationNumberValid ||
                      translate('errors.counterpartyRegistrationNumberBG')
                    );
                  }
                  if (watchedCounterpartyVatNumber.startsWith('BG')) {
                    return (
                      isCounterpartyRegistrationNumberValid ||
                      translate('errors.counterpartyRegistrationNumberBG')
                    );
                  }

                  return (
                    isCounterpartyRegistrationNumberValid ||
                    translate('errors.counterpartyRegistrationNumberForeign')
                  );
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!isCounterpartyRegistrationNumberValid}
                  label={translate('labels.companyRegistrationNumber')}
                  name="counterpartyRegistrationNumber"
                  placeholder={translate(
                    'labels.counterpartyRegistrationNumber'
                  )}
                  helperText={
                    !isCounterpartyRegistrationNumberValid &&
                    (watchedCounterpartyVatNumber?.startsWith('BG')
                      ? translate('errors.counterpartyRegistrationNumberBG')
                      : watchedCounterpartyVatNumber === ''
                        ? translate('errors.counterpartyRegistrationNumberBG')
                        : translate(
                            'errors.counterpartyRegistrationNumberForeign'
                          ))
                  }
                  sx={verificationFormItem}
                />
              )}
            />
            <Controller
              name="counterpartyVatNumber"
              control={control}
              rules={{
                validate: (value) => {
                  if (value?.startsWith('BG')) {
                    return (
                      isCounterpartyVatValid ||
                      translate('errors.counterpartyVatNumberEquality')
                    );
                  }
                  return true; // No validation error for non-BG VAT numbers
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={translate('labels.vatNumber')}
                  name="counterpartyVatNumber"
                  placeholder={translate('labels.vatNumber')}
                  sx={verificationFormItem}
                  error={!!field.value && !isCounterpartyVatValid}
                  helperText={
                    field.value &&
                    !isCounterpartyVatValid &&
                    translate('errors.counterpartyVatNumberEquality')
                  }
                />
              )}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
